import { err, type ResultErr, type ResultError } from "./result";

type ResultErrTagged<R extends Record<string, unknown>> = ResultErr<
	Omit<ResultError, "data"> & { data: { type: string } & R }
> & { success: false };

type ResultErrorTaggedData<R extends Record<string, unknown>> = Omit<
	ResultError,
	"data"
> & { data: { type: string } & R };

function createTaggedErr<R extends Record<string, unknown>>(
	type: string,
	config: Pick<ResultError, "message">,
): {
	err: (
		error: Partial<Pick<ResultError, "message">> &
			Omit<ResultError, "data" | "message"> & { data: R },
	) => ResultErrTagged<R>;
	match: <T>(
		fn: (error: Pick<ResultError, "message"> & { data: R }) => T,
	) => (error: Pick<ResultError, "message"> & { data: R }) => T;
	is: (error: ResultError) => error is ResultErrorTaggedData<R>;
};
function createTaggedErr<R extends Record<string, unknown>>(
	type: string,
): {
	err: (error: Omit<ResultError, "data"> & { data: R }) => ResultErrTagged<R>;
	match: <T>(
		fn: (error: Pick<ResultError, "message"> & { data: R }) => T,
	) => (error: Pick<ResultError, "message"> & { data: R }) => T;
	is: (error: ResultError) => error is ResultErrorTaggedData<R>;
};
function createTaggedErr<R extends Record<string, unknown>>(
	type: string,
	config?: Pick<ResultError, "message">,
) {
	return {
		err: (error: Omit<ResultError, "data"> & { data: R }) =>
			err({
				...config,
				...error,
				data: { type, ...error.data },
			}) as ResultErrTagged<R>,
		match: <T>(fn: (error: Pick<ResultError, "message"> & { data: R }) => T) =>
			fn,
		is: (error: ResultError): error is ResultErrorTaggedData<R> =>
			(error.data as { type?: string })?.type === type,
	};
}

export { createTaggedErr };
